import React from "react";
import { Video, Mic, Eye, Heart, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import IntentIcon from "./IntentIcon";

interface StageCardProps {
  id: string;
  name: string;
  topic?: string;
  cityName?: string;
  countryFlag?: string;
  participantsCount: number;
  maxParticipants: number;
  isVideoEnabled: boolean;
  hostName?: string;
  hostPhoto?: string;
  intent?: string;
  participantPhotos?: string[];
  onWatch: () => void;
  onJoin?: () => void;
  variant?: "compact" | "full";
}

const StageCard: React.FC<StageCardProps> = ({
  id,
  name,
  topic,
  cityName,
  countryFlag,
  participantsCount,
  maxParticipants,
  isVideoEnabled,
  hostName,
  hostPhoto,
  intent,
  participantPhotos = [],
  onWatch,
  onJoin,
  variant = "compact",
}) => {
  const isFull = participantsCount >= maxParticipants;

  if (variant === "compact") {
    return (
      <button
        onClick={onWatch}
        className={cn(
          "w-full flex items-center gap-3 p-3 rounded-2xl",
          "bg-white/70 hover:bg-white/90 transition-all",
          "border border-white/60 hover:border-pink-200",
          "text-left group hover-lift press-scale"
        )}
      >
        {/* Host avatar with video/voice badge */}
        <div className="relative flex-shrink-0">
          {hostPhoto ? (
            <img
              src={hostPhoto}
              alt={hostName || "Host"}
              className="w-11 h-11 rounded-full object-cover ring-2 ring-pink-500/20"
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center">
              <span className="text-white font-semibold text-sm">
                {hostName?.charAt(0) || "?"}
              </span>
            </div>
          )}
          {/* Live pulse */}
          <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500 border-2 border-white" />
          </span>
        </div>

        {/* Stage info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <p className="font-semibold text-foreground truncate text-sm">{name}</p>
            {intent && <IntentIcon intent={intent} size="sm" />}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="text-base">{countryFlag}</span>
            <span className="truncate">{cityName}</span>
            <span>·</span>
            <span className="flex items-center gap-0.5">
              {isVideoEnabled ? (
                <Video className="w-3 h-3" />
              ) : (
                <Mic className="w-3 h-3" />
              )}
            </span>
          </div>
        </div>

        {/* Participant bubbles */}
        {participantPhotos.length > 0 && (
          <div className="flex -space-x-2 mr-1">
            {participantPhotos.slice(0, 3).map((photo, idx) => (
              <img
                key={idx}
                src={photo}
                alt=""
                className="w-7 h-7 rounded-full object-cover ring-2 ring-white"
              />
            ))}
            {participantsCount > 3 && (
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center ring-2 ring-white">
                <span className="text-[10px] font-medium text-muted-foreground">
                  +{participantsCount - 3}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Watch indicator */}
        <div className="flex items-center gap-1 text-pink-500 opacity-0 group-hover:opacity-100 transition-opacity">
          <Eye className="w-4 h-4" />
        </div>
      </button>
    );
  }

  // Full variant
  return (
    <div
      className={cn(
        "p-4 rounded-2xl",
        "bg-white/70 backdrop-blur-sm",
        "border border-white/60",
        "hover-lift"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">{countryFlag}</span>
          <span className="text-sm text-muted-foreground">{cityName}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
          </span>
          <span className="text-xs font-medium text-green-600">Live</span>
        </div>
      </div>

      {/* Stage name */}
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-bold text-foreground text-lg">{name}</h3>
        {intent && <IntentIcon intent={intent} size="md" />}
      </div>

      {/* Topic/description */}
      {topic && (
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{topic}</p>
      )}

      {/* Participants preview */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex -space-x-2">
          {hostPhoto && (
            <img
              src={hostPhoto}
              alt={hostName || "Host"}
              className="w-9 h-9 rounded-full object-cover ring-2 ring-white"
            />
          )}
          {participantPhotos.slice(0, 4).map((photo, idx) => (
            <img
              key={idx}
              src={photo}
              alt=""
              className="w-9 h-9 rounded-full object-cover ring-2 ring-white"
            />
          ))}
        </div>
        <span className="text-sm text-muted-foreground">
          {participantsCount} {participantsCount === 1 ? "person" : "people"} here
        </span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={onWatch}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
            "bg-muted/50 hover:bg-muted/80 transition-colors",
            "text-foreground font-medium text-sm"
          )}
        >
          <Eye className="w-4 h-4" />
          Watch
        </button>
        {onJoin && !isFull && (
          <button
            onClick={onJoin}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl",
              "bg-gradient-to-r from-pink-500 to-rose-500",
              "hover:from-pink-600 hover:to-rose-600 transition-colors",
              "text-white font-medium text-sm"
            )}
          >
            <Heart className="w-4 h-4 fill-white" />
            Join
          </button>
        )}
      </div>
    </div>
  );
};

export default StageCard;
